/**
 * Oroboros product brand — isolated from Jester `brand.config`.
 * When this frontend splits out of grail, this file (and `api/`) move with it.
 */

export type OroborosBrand = {
  name: string;
  shortName: string;
  /** One-line product positioning under the hero wordmark. */
  tagline: string;
  /** Landing path. Dedicated host is `/`; Grail in-process stays `/oroboros`. */
  path: string;
  apiBaseUrl: string;
  authBaseUrl: string;
  /** Local Oroboros docs hub (`/docs` on the dedicated host). */
  docsUrl: string;
  siteUrl: string;
  /** Parent platform site (Jester) — footer / powered-by. */
  jesterSiteUrl: string;
  jesterTelegramUrl: string;
  jesterXUrl: string;
  termsUrl: string;
  privacyUrl: string;
  terminalPath: string;
  /** Top-bar / chat mark. `markPath` is the 1× WebP, `markSrcSet` adds the 2× version. */
  markPath: string;
  markSrcSet: string;
  /** Hero ring. `heroPath` is the 800 px WebP, `heroSrcSet` adds the 1600 px version for retina. */
  heroPath: string;
  heroSrcSet: string;
  /** Full-bleed nebula plate behind Oroboros root / splash (WebP; the PNG stays for the branding studio). */
  nebulaStartPath: string;
  nebulaStartSrcSet: string;
  jesterMarkPath: string;
};

const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() || '';
/** Empty unless `VITE_AUTH_ORIGIN` is set. Login is this app; Castellan is a different service. */
const authBase = String(import.meta.env.VITE_AUTH_ORIGIN ?? '')
  .trim()
  .replace(/\/$/, '');
const siteUrl =
  (import.meta.env.VITE_OROBOROS_SITE_URL as string | undefined)?.trim() ||
  (import.meta.env.VITE_OUROBOROS_SITE_URL as string | undefined)?.trim() ||
  (typeof window !== 'undefined' ? window.location.origin : '');
const jesterSiteUrl =
  (import.meta.env.VITE_JESTER_WWW_URL as string | undefined)?.trim() || 'https://www.jester.trade';

function sameOriginLegalUrl(path: string): string {
  const origin = siteUrl.replace(/\/$/, '');
  const rest = path.replace(/^\/+/, '');
  return origin ? `${origin}/${rest}` : `/${rest}`;
}

export const oroborosBrand: OroborosBrand = {
  name: 'Oroboros',
  shortName: 'Oroboros',
  tagline: 'Autonomous trading terminal. Perpetual markets.',
  path: '/',
  apiBaseUrl: apiBase,
  authBaseUrl: authBase,
  docsUrl:
    (import.meta.env.VITE_OROBOROS_DOCS_URL as string | undefined)?.trim() ||
    (import.meta.env.VITE_OUROBOROS_DOCS_URL as string | undefined)?.trim() ||
    '/docs',
  siteUrl,
  jesterSiteUrl,
  jesterTelegramUrl: 'https://t.me/JestTech',
  jesterXUrl: 'https://x.com/jesttech',
  termsUrl:
    (import.meta.env.VITE_TERMS_URL as string | undefined)?.trim() ||
    sameOriginLegalUrl('terms'),
  privacyUrl:
    (import.meta.env.VITE_PRIVACY_URL as string | undefined)?.trim() || `${jesterSiteUrl}/privacy`,
  terminalPath:
    (import.meta.env.VITE_OROBOROS_TERMINAL_PATH as string | undefined)?.trim() ||
    (import.meta.env.VITE_OUROBOROS_TERMINAL_PATH as string | undefined)?.trim() ||
    '/app',
  markPath: '/terminal/assets/oroboros/logo-96.webp',
  markSrcSet: '/terminal/assets/oroboros/logo-96.webp 1x, /terminal/assets/oroboros/logo-256.webp 2x',
  heroPath: '/terminal/assets/oroboros/torq-800.webp',
  heroSrcSet: '/terminal/assets/oroboros/torq-800.webp 800w, /terminal/assets/oroboros/torq-1600.webp 1600w',
  nebulaStartPath: '/terminal/assets/oroboros/nebula-1024.webp',
  nebulaStartSrcSet:
    '/terminal/assets/oroboros/nebula-640.webp 640w, /terminal/assets/oroboros/nebula-1024.webp 1024w',
  jesterMarkPath: '/terminal/assets/jester_main_sq_logo_hollow.svg',
};

export default oroborosBrand;
